import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

// Months dropdown options
const months = [
    { value: 1, name: "January" }, { value: 2, name: "February" }, { value: 3, name: "March" },
    { value: 4, name: "April" }, { value: 5, name: "May" }, { value: 6, name: "June" },
    { value: 7, name: "July" }, { value: 8, name: "August" }, { value: 9, name: "September" },
    { value: 10, name: "October" }, { value: 11, name: "November" }, { value: 12, name: "December" }
];

// Returns current and previous year for dropdown
const getYears = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1];
};

// Initial empty state for stats
const initialStats = {
    SolarPowerGenerated: '',
    SolarSavings: '',
    WaterExtractedBoreholes: '',
    WaterSavings: ''
};

const TempSustainabilityStats = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [stats, setStats] = useState(initialStats);
    const [isLoading, setIsLoading] = useState(false);

    const { apiFetch } = useAuth();
    const years = getYears();

    // Disable future months if current year is selected
    const isMonthDisabled = (monthValue) => {
        if (selectedYear > currentYear) return false;
        if (selectedYear === currentYear && monthValue > currentMonth) return true;
        return false;
    };

    // Create period in YYYYMM format
    const getSelectedPeriod = () =>
        Number(`${selectedYear}${selectedMonth.toString().padStart(2, '0')}`);

    // Fetch stats for selected period
    useEffect(() => {
        const fetchStats = async () => {
            setIsLoading(true);
            try {
                const response = await apiFetch(`/api/Ops/TempSustainabilityStats?period=${getSelectedPeriod()}`);
                const data = await response.json();
                if (data.success && data.data) {
                    setStats({
                        SolarPowerGenerated: data.data.solarPowerGenerated ?? '',
                        SolarSavings: data.data.solarSavings ?? '',
                        WaterExtractedBoreholes: data.data.waterExtractedBoreholes ?? '',
                        WaterSavings: data.data.waterSavings ?? ''
                    });
                } else {
                    setStats(initialStats);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                setStats(initialStats);
            } finally {
                setIsLoading(false);
            }
        };
        fetchStats();
        // eslint-disable-next-line
    }, [selectedMonth, selectedYear]);

    // Handle input changes, only allow numbers and decimals
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Only allow numbers and decimals
        if (value === '' || /^\d+(\.\d{0,4})?$/.test(value)) {
            setStats(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Save button handler
    const handleSave = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const payload = {
                period: getSelectedPeriod(),
                solarPowerGenerated: stats.SolarPowerGenerated === '' ? null : parseFloat(stats.SolarPowerGenerated),
                solarSavings: stats.SolarSavings === '' ? null : parseFloat(stats.SolarSavings),
                waterExtractedBoreholes: stats.WaterExtractedBoreholes === '' ? null : parseFloat(stats.WaterExtractedBoreholes),
                waterSavings: stats.WaterSavings === '' ? null : parseFloat(stats.WaterSavings)
            };
            const response = await apiFetch('/api/Ops/TempSustainabilityStats', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Data saved successfully!');
            } else {
                toast.error('Failed to save data.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="temp-sustainability-stats-container" style={{ paddingBottom: "15px" }}>
            <h1>Temp Sustainability Stats</h1>
            <div className="period-selector">
                <label>Month:</label>
                <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}>
                    {months.map(month => (
                        <option key={month.value} value={month.value} disabled={isMonthDisabled(month.value)}>
                            {month.name}
                        </option>
                    ))}
                </select>
                <label>Year:</label>
                <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
                    {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </select>
            </div>
            <form onSubmit={handleSave}>
                <div style={{ paddingBottom: '10px' }}>
                    <label>Solar Power Generated</label>
                    <input
                        type="text"
                        name="SolarPowerGenerated"
                        value={stats.SolarPowerGenerated}
                        onChange={handleInputChange}
                        inputMode="decimal"
                        placeholder="Solar Power Generated"
                    />
                </div>
                <div style={{ paddingBottom: '10px' }}>
                    <label>Solar Savings</label>
                    <input
                        type="text"
                        name="SolarSavings"
                        value={stats.SolarSavings}
                        onChange={handleInputChange}
                        inputMode="decimal"
                        placeholder="Solar Savings"
                    />
                </div>
                <div style={{ paddingBottom: '10px' }}>
                    <label>Water Extracted Boreholes</label>
                    <input
                        type="text"
                        name="WaterExtractedBoreholes"
                        value={stats.WaterExtractedBoreholes}
                        onChange={handleInputChange}
                        inputMode="decimal"
                        placeholder="Water Extracted Boreholes"
                    />
                </div>
                <div style={{ paddingBottom: '10px' }}>
                    <label>Water Savings</label>
                    <input
                        type="text"
                        name="WaterSavings"
                        value={stats.WaterSavings}
                        onChange={handleInputChange}
                        inputMode="decimal"
                        placeholder="Water Savings"
                    />
                </div>
                <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Saving...' : 'Save'}
                </button>
            </form>

        </div>
    );
};

export default TempSustainabilityStats;